import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnInit,
    signal,
    Signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {IdentityModel} from '../../identity/identity.model';
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {QrCodePageComponent} from "../../ui-elements/qr-code/qr-code-page/qr-code-page.component";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {NavigationService} from "../../navigation/navigation.service";
import {DealData} from '../../deal/deal-entity.model';
import {NgClass} from '@angular/common';
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {CssVarDirective} from '../../utils/css-var.directive';
import {ElementWidthDirective} from '../../utils/element-width.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';
import {span} from "../../utils/string-utils";

@Component({
    selector: "woa-ticket",
    templateUrl: "./ticket.component.html",
    styleUrl: "./ticket.component.scss",
    imports: [
        CssVarDirective,
        ElementWidthDirective,
        IconComponent,
        ImageCacheDirective,
        NgClass,
        ResponsiveDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TicketComponent implements OnInit {
    private identityModel: IdentityModel = inject(IdentityModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private navigationService: NavigationService = inject(NavigationService);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private translate: TranslateService = inject(TranslateService);

    public readonly ticket: InputSignal<DealData> = input.required<DealData>();
    public readonly index: InputSignal<number> = input.required<number>();

    private readonly content: Signal<ElementRef | undefined> = viewChild("content");
    private readonly countdown: Signal<ElementRef | undefined> = viewChild("countdown");

    private viewReady: WritableSignal<boolean> = signal(false);

    constructor() {
        afterNextRender(() => this.viewReady.set(true));
    }

    public readonly stampRows: Signal<number> = computed<number>(() => {
        if (!this.viewReady() || !this.configurationModel.app() || this.responsiveService.windowWidth() === undefined || !this.ticket() || !this.content()?.nativeElement) {
            return 0;
        }
        const spec: string = getComputedStyle(this.content().nativeElement).getPropertyValue("--themed-ticket-max-column-spec").trim();
        const maxCols: number[] = spec.split(/[, ]/).map((s) => parseInt(s.trim()));
        for (let rows: number = 1; rows <= maxCols.length; rows++) {
            if (this.ticket().usageLimit <= (rows * maxCols[rows - 1])) {
                return rows;
            }
        }
        return 0;
    });

    public readonly stampColumns: Signal<number> = computed<number>(() => {
        if (!this.configurationModel.app() || this.responsiveService.windowWidth() === undefined || !this.ticket()) {
            return 0;
        }
        return Math.ceil(this.ticket().usageLimit / this.stampRows());
    });

    public readonly placeholderContainerSize: Signal<number> = computed<number>(() => {
        if (!this.ticket() || !this.countdown()?.nativeElement || !this.content()?.nativeElement) {
            return 0;
        }
        const cellHeight: number = Math.floor(this.countdown().nativeElement.offsetHeight / this.stampRows());
        const cellWidth: number = Math.floor(this.countdown().nativeElement.offsetWidth / this.stampColumns());
        return Math.min(cellHeight, cellWidth, 65);
    });

    public readonly stampIcon: Signal<string> = computed(() => {
        if (!this.ticket() || !this.content()?.nativeElement) {
            return "";
        }
        return getComputedStyle(this.content().nativeElement).getPropertyValue("--themed-ticket-stamp-icon").trim();
    });

    public readonly cells: Signal<Array<number>> = computed<Array<number>>(() => Array.from(Array(this.ticket().usageLimit), (_, x) => x));
    public readonly cardTheme: Signal<string> = computed(() => this.ticket().theme ? this.ticket().theme : "default");
    public readonly usageCount: Signal<number> = computed<number>(() => this.identityModel.ticketUsage[this.ticket().dealCode]);

    ngOnInit(): void {
        this.identityModel.ticketUsage[this.ticket().dealCode] = Math.max(this.ticket().usageCount, this.identityModel.ticketUsage[this.ticket().dealCode] || 0);
    }

    getStampRotation(stampNr: number): string {
        return (((this.ticket().title.charCodeAt(stampNr % this.ticket().title.length) * 13) % 360) - 180) + "deg";
    }

    async showQrCode(): Promise<void> {
        await QrCodePageComponent.showQrCode(this.navigationService, this.ticket().dealCode, this.translate.instant("woa.label.ticket.qrCode"));
        this.identityModel.ticketUsage[this.ticket().dealCode]++;
    }

    protected readonly span = span;
}
